import {useState, useEffect} from 'react';
import {Table, Tag, Button} from 'antd';
import {HolderOutlined} from '@ant-design/icons';
import {useMutation} from '@apollo/client';
import {toast} from 'react-toastify';
import {
  SortableContainer,
  SortableElement,
  SortableHandle,
} from 'react-sortable-hoc';
import {arrayMoveImmutable} from 'array-move';
import {ConfirmModal} from 'pages/adminOpportunities/stocksListings/ViewOrders/HandleOrders';
import DBModal from 'components/modal/modal';
import {EditQuestionForm} from '../components/EditQuestionForm';
import styles from '../singleMiniFeedback.module.scss';
import {
  UPDATE_MINI_FEEDBACK_QUESTION,
  DELETE_MINI_FEEDBACK_QUESTION,
} from '../../resolvers';
import {useSelector} from 'react-redux';
import {makeSelectTabOperations} from 'redux/store/auth';
import {NavTabsEnum} from 'utils/constants';

const DragHandle = SortableHandle(() => (
  <div className={styles.dragIcon} style={{cursor: 'grab', display: 'flex'}}>
    <HolderOutlined />
  </div>
));

const SortableItem = SortableElement(props => <tr {...props} />);
const SortableBody = SortableContainer(props => <tbody {...props} />);

export const QuestionsTab = ({
  questions,
  feedbackId,
  featureId,
  refetch,
  baseLanguage,
}) => {
  const [items, setItems] = useState([]);
  const [isDirty, setIsDirty] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deleteModal, setDeleteModal] = useState({
    open: false,
    questionId: null,
  });
  const [editModal, setEditModal] = useState({
    open: false,
    question: null,
  });
  const tabOperations = useSelector(state =>
    makeSelectTabOperations(state, NavTabsEnum.FAQ),
  );

  const [deleteQuestion, {loading: isDeleting}] = useMutation(
    DELETE_MINI_FEEDBACK_QUESTION,
    {
      onCompleted: () => {
        toast.success('Question deleted successfully.');
        setDeleteModal({open: false, questionId: null});
        if (refetch) refetch();
      },
      onError: err => {
        toast.error(err.message || 'An error occurred while deleting.');
      },
    },
  );

  const [updateQuestions, {loading: isUpdating}] = useMutation(
    UPDATE_MINI_FEEDBACK_QUESTION,
    {
      onCompleted: () => {
        toast.success('Question order updated successfully.');
        setIsModalOpen(false);
        setIsDirty(false);
        if (refetch) refetch();
      },
      onError: err => {
        toast.error(err.message || 'An error occurred while saving.');
      },
    },
  );

  useEffect(() => {
    if (questions) {
      setItems([...questions].sort((a, b) => a.order - b.order));
      setIsDirty(false);
    }
  }, [questions]);

  const onSortEnd = ({oldIndex, newIndex}) => {
    if (oldIndex === newIndex) return;

    const newItems = arrayMoveImmutable(items, oldIndex, newIndex);
    const updatedItems = newItems.map((item, idx) => ({
      ...item,
      order: idx + 1,
    }));

    setItems(updatedItems);
    setIsDirty(true);
  };

  const handleSaveChanges = () => {
    const updatedPayload = items.map(item => {
      const payload = {
        id: item.id,
        questionText: item.questionText,
        questionType: item.questionType,
        order: item.order,
        options: item.options
          ? item.options.map(opt => ({
              label: opt.label,
              value: opt.value,
            }))
          : [],
      };
      if (item.multilang?.length) {
        payload.multilang = item.multilang.map(m => ({
          language: m.language,
          questionText: m.questionText || null,
          ...(m.options?.length
            ? {
                options: m.options.map(o => ({
                  label: o.label,
                  value: o.value,
                })),
              }
            : {}),
        }));
      }
      return payload;
    });

    updateQuestions({
      variables: {
        input: {
          feedbackId,
          questions: updatedPayload,
        },
      },
    });
  };

  const columns = [
    {
      title: 'Order',
      key: 'order',
      width: 100,
      render: (_, __, index) => (
        <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
          <DragHandle />
          <div className={styles.questionNumber} style={{margin: 0}}>
            {items[index].order}
          </div>
        </div>
      ),
    },
    {
      title: 'Question',
      dataIndex: 'questionText',
      key: 'questionText',
      className: 'drag-visible',
    },
    {
      title: 'Type',
      key: 'type',
      width: 150,
      render: (_, record) => (
        <div style={{display: 'flex', gap: 8}}>
          <Tag className={styles.typeTag}>
            {record.questionType
              ? record.questionType
                  .toLowerCase()
                  .replace(/_/g, ' ')
                  .replace(/\b\w/g, char => char.toUpperCase())
              : 'Option'}
          </Tag>
        </div>
      ),
    },
    {
      title: 'Options',
      dataIndex: 'options',
      key: 'options',
      width: 300,
      render: (_, record) => (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '8px',
          }}>
          {record.options?.length > 0 ? (
            record.options?.map((opt, oIndex) => (
              <div key={oIndex} className={styles.optionBox}>
                {opt.label}
              </div>
            ))
          ) : (
            <div>N/A</div>
          )}
        </div>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <div style={{display: 'flex', gap: '8px'}}>
          <Button
            type="link"
            disabled={!tabOperations?.update}
            onClick={() => setEditModal({open: true, question: record})}>
            Edit
          </Button>
          <Button
            type="link"
            danger
            disabled={!tabOperations?.delete}
            onClick={() => setDeleteModal({open: true, questionId: record.id})}>
            Delete
          </Button>
        </div>
      ),
    },
  ];

  const DraggableContainer = props => (
    <SortableBody
      useDragHandle
      disableAutoscroll
      helperClass="row-dragging"
      onSortEnd={onSortEnd}
      {...props}
    />
  );

  const DraggableBodyRow = ({...restProps}) => {
    const index = items.findIndex(x => x.id === restProps['data-row-key']);
    return <SortableItem index={index} {...restProps} />;
  };

  if (!questions || questions.length === 0) {
    return (
      <div className={styles.emptyQuestions}>
        No questions available for this feedback.
      </div>
    );
  }

  return (
    <div>
      {isDirty && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            marginBottom: 16,
          }}>
          <Button
            type="primary"
            disabled={!tabOperations?.update}
            style={{
              background: '#8b5cf6',
              borderColor: '#8b5cf6',
              height: 40,
              borderRadius: 8,
              padding: '0 24px',
            }}
            onClick={() => setIsModalOpen(true)}>
            Save Order
          </Button>
        </div>
      )}

      <Table
        pagination={false}
        dataSource={items}
        columns={columns}
        rowKey="id"
        className="antd-table"
        components={{
          body: {
            wrapper: DraggableContainer,
            row: DraggableBodyRow,
          },
        }}
        expandable={{
          expandedRowRender: record => (
            <div
              className={styles.optionsRow}
              style={{paddingLeft: 40, marginTop: 0}}>
              {['RADIO', 'OPTION', 'DROPDOWN'].includes(record.questionType) ? (
                record.options?.map((opt, oIndex) => (
                  <div key={oIndex} className={styles.optionBox}>
                    {opt.label}{' '}
                  </div>
                ))
              ) : (
                <div className={styles.optionBox}>
                  {record.questionType
                    ? record.questionType
                        .toLowerCase()
                        .replace(/_/g, ' ')
                        .replace(/\b\w/g, char => char.toUpperCase()) +
                      ' response'
                    : 'Text response'}
                </div>
              )}
            </div>
          ),
          defaultExpandAllRows: true,
          expandIconColumnIndex: -1,
        }}
      />

      {isModalOpen && (
        <DBModal
          isOpen={true}
          handleClose={() => setIsModalOpen(false)}
          width={500}
          content={
            <div style={{padding: '10px 0'}}>
              <h2 style={{fontSize: 20, marginBottom: 16}}>Confirm Changes</h2>
              <p
                style={{
                  color: '#4b5563',
                  marginBottom: 24,
                  fontSize: 15,
                  lineHeight: 1.5,
                }}>
                You are about to save the new order for the feedback questions.
                Are you sure you want to proceed?
              </p>

              <div
                style={{display: 'flex', justifyContent: 'flex-end', gap: 12}}>
                <Button
                  onClick={() => setIsModalOpen(false)}
                  style={{height: 40, borderRadius: 8}}
                  disabled={isUpdating}>
                  Cancel
                </Button>
                <Button
                  type="primary"
                  style={{
                    background: '#8b5cf6',
                    borderColor: '#8b5cf6',
                    height: 40,
                    borderRadius: 8,
                  }}
                  onClick={handleSaveChanges}
                  loading={isUpdating}>
                  Confirm & Save
                </Button>
              </div>
            </div>
          }
        />
      )}

      {deleteModal.open && (
        <ConfirmModal
          openModal={deleteModal.open}
          setOpenModal={() => setDeleteModal({open: false, questionId: null})}
          loading={isDeleting}
          title="Delete Question"
          confirmText="Are you sure you want to delete this question? This action cannot be undone!"
          handleOperation={() => {
            deleteQuestion({
              variables: {
                input: {
                  id: deleteModal.questionId,
                  featureId: featureId,
                },
              },
            });
          }}
        />
      )}

      {editModal?.open && editModal?.question && (
        <DBModal
          isOpen={editModal.open}
          handleClose={() => setEditModal({open: false, question: null})}
          width={900}
          content={
            <EditQuestionForm
              feedbackId={feedbackId}
              question={editModal.question}
              allQuestions={items}
              baseLanguage={baseLanguage}
              closeModal={() => setEditModal({open: false, question: null})}
              refetch={refetch}
            />
          }
        />
      )}
    </div>
  );
};
